// src/components/PendenzenBoard.jsx
// Planner-artiges Board: Spalten (Kategorien oder Projekte) mit Pendenz-Cards.
// Karten sind per HTML5-Drag&Drop zwischen Spalten verschiebbar (wenn onDropCard
// gesetzt ist). Spalten können hinzugefügt/umbenannt/gelöscht werden (custom).
import React, { useState } from "react";
import { sanitizeRichText } from "../utils/richText";

const ART_LABEL = { P: "Pendenz", E: "Entscheid", N: "Notiz" };
const STATUS_LABEL = {
  offen: "Offen",
  inBearbeitung: "In Bearbeitung",
  kritisch: "Kritisch",
  erledigt: "Erledigt",
};

const todayIso = () => {
  const d = new Date();
  return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" });
};
const massgeblicherTermin = (p) => p.terminNeu || p.terminSoll || "";
const istUeberfaellig = (p) => {
  if (p.status === "erledigt") return false;
  const t = massgeblicherTermin(p);
  return t ? t < todayIso() : false;
};

// --- Einzelne Pendenz-Card ------------------------------------------------
const PendenzCard = ({
  pendenz: p,
  projectLabel,
  onToggleChecklistItem,
  hasPlan,
  onOpenPlan,
}) => {
  const ueberfaellig = istUeberfaellig(p);
  const erledigt = p.status === "erledigt";
  const termin = massgeblicherTermin(p);
  const checkliste = p.checkliste || [];
  const doneCount = checkliste.filter((i) => i.done).length;

  return (
    <div className={`pcard${erledigt ? " is-erledigt" : ""} pcard--${p.status}`}>
      <div className="pcard__top">
        <span className={`art-badge art-badge--${p.typ}`} title={ART_LABEL[p.typ] || p.typ}>
          {p.typ}
        </span>
        <span className="pcard__nr">Nr. {p.nummer}</span>
        <span className={`status-badge status-badge--${p.status}`}>
          {STATUS_LABEL[p.status] || p.status}
        </span>
      </div>

      <div
        className="pcard__desc rte-content"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p.beschreibung) }}
      />

      {(p.themen || []).length > 0 && (
        <div className="pcard__chips">
          {p.themen.map((t) => (
            <span key={t} className="thema-chip">
              {t}
            </span>
          ))}
        </div>
      )}

      {(projectLabel || !p.sichtbarExtern) && (
        <div className="pcard__chips">
          {projectLabel && <span className="pcard__project">{projectLabel}</span>}
          {!p.sichtbarExtern && <span className="intern-badge">intern</span>}
        </div>
      )}

      {checkliste.length > 0 && (
        <ul className="checklist__items checklist__items--compact pcard__checklist">
          {checkliste.map((item) => (
            <li key={item.id} className={`checklist__item${item.done ? " is-done" : ""}`}>
              {onToggleChecklistItem ? (
                <button
                  type="button"
                  className="checklist__toggle"
                  draggable={false}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleChecklistItem(p, item.id);
                  }}
                  title={item.done ? "Als offen markieren" : "Als erledigt markieren"}
                >
                  {item.done ? "●" : "○"}
                </button>
              ) : (
                <span className="checklist__toggle">{item.done ? "●" : "○"}</span>
              )}
              <span className="checklist__text">{item.text}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="pcard__foot">
        {termin && (
          <span className={`pcard__termin${ueberfaellig ? " termin-ueberfaellig" : ""}`}>
            {formatDate(termin)}
          </span>
        )}
        {p.verantwortlich && (
          <span className="pcard__resp" title={p.verantwortlich}>
            {p.verantwortlich}
          </span>
        )}
        {checkliste.length > 0 && (
          <span className="pcard__chkcount">
            {doneCount}/{checkliste.length}
          </span>
        )}
        {hasPlan && (
          <button
            type="button"
            className="pcard__plan-btn"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onOpenPlan?.(p);
            }}
            title="Plan mit Markierung öffnen"
          >
            Plan
          </button>
        )}
      </div>
    </div>
  );
};

// --- Spaltenkopf mit optionalem Umbenennen/Löschen ------------------------
const ColumnHead = ({ col, count, onRename, onDelete, onAddCard }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(col.label);
  const canManage = !col.fixed && (onRename || onDelete);

  const save = () => {
    const t = label.trim();
    setEditing(false);
    if (t && t !== col.label) onRename?.(col.key, t);
    else setLabel(col.label);
  };

  return (
    <div className="pboard__col-head">
      {editing ? (
        <input
          className="pboard__col-title-input"
          value={label}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); save(); }
            else if (e.key === "Escape") { setLabel(col.label); setEditing(false); }
          }}
        />
      ) : (
        <span
          className="pboard__col-title"
          onDoubleClick={() => canManage && onRename && setEditing(true)}
          title={canManage && onRename ? "Doppelklick zum Umbenennen" : undefined}
        >
          {col.label}
        </span>
      )}
      <span className="pboard__col-count">{count}</span>
      {onAddCard && !editing && (
        <button
          type="button"
          className="pboard__col-add"
          title="Pendenz in dieser Spalte erfassen"
          aria-label="Pendenz hinzufügen"
          onClick={() => onAddCard(col.key)}
        >
          +
        </button>
      )}
      {canManage && onDelete && !editing && (
        <button
          type="button"
          className="pboard__col-delete"
          title="Kategorie löschen"
          onClick={() => onDelete(col.key)}
        >
          ×
        </button>
      )}
    </div>
  );
};

// --- Board ----------------------------------------------------------------
const PendenzenBoard = ({
  columns,
  itemsByColumn,
  renderCard,
  onCardClick,
  onDropCard, // (cardId, toColumnKey) => void  – aktiviert Drag&Drop
  onAddColumn, // (label) => void – zeigt "Kategorie hinzufügen"
  onRenameColumn,
  onDeleteColumn,
  onAddCard, // (columnKey) => void – dezentes "+" je Spalte zum Erfassen
}) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const commitAdd = () => {
    const t = newLabel.trim();
    if (t) onAddColumn?.(t);
    setNewLabel("");
    setAdding(false);
  };

  return (
    <div className="pboard">
      {columns.map((col) => {
        const items = itemsByColumn[col.key] || [];
        const dnd = !!onDropCard;
        return (
          <div
            key={col.key}
            className={`pboard__col${overCol === col.key ? " is-over" : ""}`}
            onDragOver={dnd ? (e) => { e.preventDefault(); setOverCol(col.key); } : undefined}
            onDragLeave={dnd ? () => setOverCol((c) => (c === col.key ? null : c)) : undefined}
            onDrop={
              dnd
                ? (e) => {
                    e.preventDefault();
                    if (dragId) onDropCard(dragId, col.key);
                    setDragId(null);
                    setOverCol(null);
                  }
                : undefined
            }
          >
            <ColumnHead
              col={col}
              count={items.length}
              onRename={onRenameColumn}
              onDelete={onDeleteColumn}
              onAddCard={onAddCard}
            />
            <div className="pboard__cards">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="pboard__card-wrap"
                  draggable={dnd}
                  onDragStart={dnd ? () => setDragId(item.id) : undefined}
                  onDragEnd={dnd ? () => { setDragId(null); setOverCol(null); } : undefined}
                  onClick={() => onCardClick?.(item)}
                >
                  {renderCard(item)}
                </div>
              ))}
              {items.length === 0 && <div className="pboard__empty">–</div>}
            </div>
          </div>
        );
      })}

      {onAddColumn && (
        <div className="pboard__col pboard__col--add">
          {adding ? (
            <div className="pboard__add-form">
              <input
                value={newLabel}
                autoFocus
                placeholder="Kategoriename…"
                onChange={(e) => setNewLabel(e.target.value)}
                onBlur={commitAdd}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitAdd(); }
                  else if (e.key === "Escape") { setNewLabel(""); setAdding(false); }
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              className="pboard__add-btn"
              onClick={() => setAdding(true)}
            >
              + Kategorie hinzufügen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { PendenzCard };
export default PendenzenBoard;
